import { useQuery } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  where,
  type QueryConstraint,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import * as React from 'react';
import { firestore, functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import { useEffectiveUid, useIsAdminMode } from '../auth/TargetUidContext';

export interface CallSummary {
  id: string;
  startedAt?: { toDate(): Date };
  endedAt?: { toDate(): Date };
  durationSec?: number;
  callerNumber?: string;
  calleeNumber?: string;
  provider?: 'twilio' | 'telnyx';
  outcome?: string;
  userTurnCount?: number;
  assistantTurnCount?: number;
  toolCallCount?: number;
  latencyP50?: number;
  latencyP95?: number;
  fillerInjections?: number;
  llmProviderUsed?: string;
  llmModelUsed?: string;
}

export interface CallEvent {
  id: string;
  type: string;
  ts_ms?: number;
  iso_ts?: { toDate(): Date };
  data?: Record<string, unknown>;
}

/** Wrap an ISO string into a `{ toDate(): Date }` so admin-mode results
 *  match the Firestore snapshot shape used by the UI. */
function isoFieldToTs<T extends { startedAt?: unknown; endedAt?: unknown; iso_ts?: unknown }>(
  obj: T,
): T {
  const out = { ...obj } as Record<string, unknown>;
  for (const key of ['startedAt', 'endedAt', 'iso_ts']) {
    const v = out[key];
    if (typeof v === 'string') {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) {
        out[key] = { toDate: () => d };
      }
    }
  }
  return out as T;
}

export interface UseCallsOptions {
  limit?: number;
  provider?: 'twilio' | 'telnyx';
}

/**
 * Live-streaming list of the user's calls when in self-mode (Firestore
 * onSnapshot). In admin-mode, polls the `adminListCalls` callable so we
 * don't have to widen Firestore Security Rules.
 */
export function useCalls(options: UseCallsOptions = {}) {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  const [items, setItems] = React.useState<CallSummary[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!user || !targetUid) return;
    if (adminMode) {
      let cancelled = false;
      let timer: number | undefined;
      const fetchOnce = async () => {
        try {
          const fn = httpsCallable<
            { uid: string; limit?: number; provider?: 'twilio' | 'telnyx' },
            { items: CallSummary[] }
          >(functions, 'adminListCalls');
          const payload: { uid: string; limit: number; provider?: 'twilio' | 'telnyx' } = {
            uid: targetUid,
            limit: options.limit ?? 100,
          };
          if (options.provider) payload.provider = options.provider;
          const r = await fn(payload);
          if (!cancelled) {
            setItems(r.data.items.map(isoFieldToTs));
            setLoading(false);
          }
        } catch {
          if (!cancelled) setLoading(false);
        }
      };
      void fetchOnce();
      timer = window.setInterval(() => void fetchOnce(), 5000);
      return () => {
        cancelled = true;
        if (timer) window.clearInterval(timer);
      };
    }
    const constraints: QueryConstraint[] = [
      orderBy('startedAt', 'desc'),
      fsLimit(options.limit ?? 50),
    ];
    if (options.provider) {
      constraints.unshift(where('provider', '==', options.provider));
    }
    const q = query(
      collection(firestore, `users/${targetUid}/calls`),
      ...constraints,
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as CallSummary),
        );
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [user, targetUid, adminMode, options.limit, options.provider]);

  return { items, loading };
}

/** Live-streaming timeline for a single call. */
export function useCallEvents(callId: string | undefined) {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  const [events, setEvents] = React.useState<CallEvent[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!user || !callId || !targetUid) return;
    if (adminMode) {
      let cancelled = false;
      let timer: number | undefined;
      const fetchOnce = async () => {
        try {
          const fn = httpsCallable<{ uid: string; callId: string }, { items: CallEvent[] }>(
            functions,
            'adminListCallEvents',
          );
          const r = await fn({ uid: targetUid, callId });
          if (!cancelled) {
            setEvents(r.data.items.map(isoFieldToTs));
            setLoading(false);
          }
        } catch {
          if (!cancelled) setLoading(false);
        }
      };
      void fetchOnce();
      timer = window.setInterval(() => void fetchOnce(), 5000);
      return () => {
        cancelled = true;
        if (timer) window.clearInterval(timer);
      };
    }
    const q = query(
      collection(firestore, `users/${targetUid}/calls/${callId}/events`),
      orderBy('ts_ms', 'asc'),
      fsLimit(2000),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setEvents(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as CallEvent),
        );
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [user, targetUid, adminMode, callId]);

  return { events, loading };
}

/** Simple one-shot fetch of a single call's summary doc. */
export function useCall(callId: string | undefined) {
  const { user } = useUser();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useQuery<CallSummary | null>({
    queryKey: ['call', targetUid, callId],
    enabled: !!user && !!callId && !!targetUid,
    queryFn: async () => {
      if (!targetUid || !callId) return null;
      if (adminMode) {
        const fn = httpsCallable<{ uid: string; callId: string }, { call: CallSummary | null }>(
          functions,
          'adminGetCall',
        );
        const r = await fn({ uid: targetUid, callId });
        return r.data.call ? isoFieldToTs(r.data.call) : null;
      }
      const ref = doc(firestore, `users/${targetUid}/calls/${callId}`);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as object) } as CallSummary;
    },
  });
}
